"use client"
import Link from "next/link"
import type { CSSProperties, ReactNode } from "react"

/**
 * Wraps a seller's name (or their whole identity block) in a link to their
 * public profile at /agents/<userId>, which lists everything they have live.
 *
 * Every surface that showed a seller's name rendered it as plain text, so there
 * was no way to get from "I like this listing" to "what else does this person
 * have". The route works for all seller types — AGENT, OWNER (FSBO) and
 * DEVELOPER — since /api/agents/[id] gates on "is publicly selling" rather than
 * "has an Agent row".
 *
 * `nested` is for cards that are themselves a Link: an <a> inside an <a> is
 * invalid HTML and browsers silently un-nest it, breaking both links. In that
 * case this renders a span that navigates on click instead.
 */
export default function SellerLink({
  agent,
  children,
  style,
  className,
  nested = false,
  title,
}: {
  agent?: { id?: string | null } | null
  children: ReactNode
  style?: CSSProperties
  className?: string
  nested?: boolean
  title?: string
}) {
  const id = agent?.id
  if (!id) return <span style={style} className={className}>{children}</span>

  const href = `/agents/${id}`

  if (nested) {
    return (
      <span
        role="link"
        tabIndex={0}
        title={title ?? "View this seller's other properties"}
        onClick={e => { e.preventDefault(); e.stopPropagation(); window.location.href = href }}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault(); e.stopPropagation(); window.location.href = href
          }
        }}
        style={{ cursor: "pointer", ...style }}
        className={className}
      >
        {children}
      </span>
    )
  }

  return (
    <Link href={href} title={title ?? "View this seller's other properties"} style={style} className={className}>
      {children}
    </Link>
  )
}
